// Thread — the per-item progress rail (item-tracking rebuild Phase 1,
// 2026-07-13). Ported from the design handoff's FullThread.dc.html; the
// geometry and state rules there are the spec.
//
// One record-backed spine (8 nodes) forks after Vendor deposit into an
// INVENTORY rail (circles — physical, marked by humans) and a MONEY rail
// (squares — derived from payment records, never clicked), rejoining at
// Complete. Encoding rules, per the design:
//   • Position/fill = physical progress only.
//   • The current-position marker is a hollow RING whose color = money
//     health (grey/yellow/green/amber/red). Rings are money; the filled
//     dots on the Calendar are delivery timing — never the same shape.
//   • Circles = physical milestones; squares = money records.
//
// Purely presentational: takes the composed state from threadStateFor()
// in utils.js. Three sizes: 'full' (item detail header, 796px),
// 'row' (Tracker table, ~205px), 'micro' (invoice rows, ~80px).

import React from 'react';
import { THREAD_SPINE_NODES, THREAD_INVENTORY_NODES, THREAD_MONEY_NODES } from '../utils';
import {
  INK_1, INK_3, INK_4, SURFACE,
  ZONE_GREEN, ZONE_GREY, ZONE_YELLOW,
  ALERT_RED_TEXT, ALERT_AMBER_TEXT, ALERT_AMBER_BORDER,
  LINE_ROW, LINE_SHELL, LINE_CONTAINER,
} from '../lib/designTokens';

// Money-health ring hexes. Amber uses the amber TEXT hue for the ring
// (matches the design's stage configs), with the border hue on due squares.
export var RING_HEX = {
  grey: ZONE_GREY,
  yellow: ZONE_YELLOW,
  green: ZONE_GREEN,
  amber: ALERT_AMBER_TEXT,
  red: ALERT_RED_TEXT,
};

// Map a money-node state ('done'|'due'|'alert'|'future') to square visuals.
function squareStyle(state, size) {
  var s = size || { done: 6, due: 7, future: 5 };
  if (state === 'done') return { w: s.done, bg: ZONE_GREEN, bd: 'none' };
  if (state === 'due') return { w: s.due, bg: SURFACE, bd: '1.5px solid ' + ALERT_AMBER_BORDER };
  if (state === 'alert') return { w: s.due, bg: SURFACE, bd: '1.5px solid ' + ALERT_RED_TEXT };
  return { w: s.future, bg: SURFACE, bd: '1px solid ' + LINE_CONTAINER };
}

function circleStyle(st, ringColor, sizes) {
  if (st === 'done') return { w: sizes.done, bg: INK_1, bd: 'none' };
  if (st === 'current') return { w: sizes.current, bg: SURFACE, bd: '1.5px solid ' + ringColor };
  return { w: sizes.future, bg: SURFACE, bd: '1px solid ' + LINE_SHELL };
}

function Marker(props) {
  // circle or square, absolutely positioned, centered on (x, y)
  var half = props.w / 2;
  return (
    <span title={props.title} style={{
      position: 'absolute', left: props.x - half, top: props.y - half,
      width: props.w, height: props.w, boxSizing: 'border-box',
      borderRadius: props.square ? 1 : '50%',
      background: props.bg, border: props.bd === 'none' ? undefined : props.bd,
    }} />
  );
}

function moneyNodeStates(money) {
  return [money.vendorBalance.state, money.finSent.state, money.finPaid.state];
}

// ── MICRO (~80px) — invoice rows, calendar popover ─────────────────────
function MicroThread(props) {
  var st = props.state;
  var stepsDone = st.inventoryIndex >= 0 ? 8 + Math.min(st.inventoryIndex, 4) : st.spineIndex;
  var pct = Math.min(1, stepsDone / 12);
  var ring = RING_HEX[st.health.ring] || ZONE_GREY;
  return (
    <span title={st.health.summary} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 56, height: 3, background: LINE_ROW, borderRadius: 1.5, position: 'relative', flexShrink: 0 }}>
        <span style={{ position: 'absolute', left: 0, top: 0, height: 3, borderRadius: 1.5,
          width: Math.round(56 * pct), background: INK_1 }} />
      </span>
      <span style={{ width: 8, height: 8, borderRadius: '50%', boxSizing: 'border-box',
        background: SURFACE, border: '1.5px solid ' + ring, flexShrink: 0 }} />
      {st.complete && <span style={{ fontSize: 9, color: ZONE_GREEN, lineHeight: 1 }}>✓</span>}
    </span>
  );
}

// ── ROW (~205px × 16px) — Tracker table ────────────────────────────────
function RowThread(props) {
  var st = props.state;
  var ring = RING_HEX[st.health.ring] || ZONE_GREY;
  var sizes = { done: 4, current: 7, future: 3 };
  var PITCH = 16, SPLIT = 10, FORK = 46, JOIN = 10;
  var spineW = PITCH * 7; // 8 nodes, 7 gaps
  var W = spineW + SPLIT + FORK + JOIN + 12;
  var midY = 8;
  var forked = st.inventoryIndex >= 0;
  var spineCurrent = forked ? 8 : st.spineIndex;

  var nodes = [];
  // spine connectors + nodes
  for (var i = 0; i < 8; i++) {
    var stKind = i < spineCurrent ? 'done' : i === spineCurrent ? 'current' : 'future';
    var c = circleStyle(stKind, ring, sizes);
    if (i > 0) {
      nodes.push(<span key={'l' + i} style={{ position: 'absolute', left: (i - 1) * PITCH, top: midY - 0.5,
        width: PITCH, height: 1, background: i <= spineCurrent ? INK_1 : LINE_ROW }} />);
    }
    nodes.push(<Marker key={'s' + i} x={i * PITCH} y={midY} w={c.w} bg={c.bg} bd={c.bd}
      title={THREAD_SPINE_NODES[i] + (i === spineCurrent && !forked ? ' — current' : '')} />);
  }

  var splitX = spineW;
  var topY = 3.5, botY = 13;
  var mStates = moneyNodeStates(st.money);
  var moneyStarted = mStates[0] === 'done';

  return (
    <span style={{ position: 'relative', display: 'inline-block', width: W, height: 16, verticalAlign: 'middle' }}>
      {nodes}
      <svg width={SPLIT} height="16" style={{ position: 'absolute', left: splitX, top: 0 }}>
        <path d={'M0 ' + midY + ' C4 ' + midY + ' 6 ' + topY + ' ' + SPLIT + ' ' + topY}
          stroke={forked ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
        <path d={'M0 ' + midY + ' C4 ' + midY + ' 6 ' + botY + ' ' + SPLIT + ' ' + botY}
          stroke={moneyStarted ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
      </svg>
      {/* inventory rail (top) */}
      {THREAD_INVENTORY_NODES.map(function(t, i) {
        var x = splitX + SPLIT + 6 + i * 11;
        var kind = st.inventoryIndex >= 0 && i < st.inventoryIndex ? 'done'
          : i === st.inventoryIndex ? 'current' : 'future';
        var c = circleStyle(kind, ring, sizes);
        return <Marker key={'i' + i} x={x} y={topY} w={c.w} bg={c.bg} bd={c.bd}
          title={t + (i === st.inventoryIndex ? ' — current' : '')} />;
      })}
      {/* money rail (bottom) */}
      {THREAD_MONEY_NODES.map(function(t, i) {
        var x = splitX + SPLIT + 7 + i * 15;
        var sq = squareStyle(mStates[i], { done: 5, due: 6, future: 4 });
        return <Marker key={'m' + i} square x={x} y={botY} w={sq.w} bg={sq.bg} bd={sq.bd}
          title={t + (mStates[i] === 'done' ? ' · done' : mStates[i] === 'future' ? '' : ' · due')} />;
      })}
      <svg width={JOIN} height="16" style={{ position: 'absolute', left: splitX + SPLIT + FORK, top: 0 }}>
        <path d={'M0 ' + topY + ' C4 ' + topY + ' 6 ' + midY + ' ' + JOIN + ' ' + midY}
          stroke={st.complete ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
        <path d={'M0 ' + botY + ' C4 ' + botY + ' 6 ' + midY + ' ' + JOIN + ' ' + midY}
          stroke={st.complete ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
      </svg>
      {st.complete
        ? <span title="Complete" style={{ position: 'absolute', left: splitX + SPLIT + FORK + JOIN + 2, top: midY - 5,
            width: 10, height: 10, borderRadius: '50%', boxSizing: 'border-box',
            border: '1.5px solid ' + ZONE_GREEN, background: SURFACE,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 6, color: ZONE_GREEN, lineHeight: 1 }}>✓</span>
        : <Marker x={splitX + SPLIT + FORK + JOIN + 6} y={midY} w={5} bg={SURFACE}
            bd={'1px solid ' + LINE_SHELL} title="Complete" />}
    </span>
  );
}

// ── FULL (796px × ~120px) — item detail header ─────────────────────────
function FullThread(props) {
  var st = props.state;
  var ring = RING_HEX[st.health.ring] || ZONE_GREY;
  var sizes = { done: 6, current: 10, future: 5 };
  var PITCH = 52, SPLIT = 24, FORK = 280, JOIN = 24;
  var spineW = PITCH * 7;
  var midY = 48, topY = 17.5, botY = 77.5;
  var forked = st.inventoryIndex >= 0;
  var spineCurrent = forked ? 8 : st.spineIndex;
  var mStates = moneyNodeStates(st.money);
  var moneyStarted = mStates[0] === 'done';
  var invX = [35, 105, 175, 245];
  var monX = [40, 150, 260];

  var els = [];
  for (var i = 0; i < 8; i++) {
    var kind = i < spineCurrent ? 'done' : i === spineCurrent ? 'current' : 'future';
    var c = circleStyle(kind, ring, sizes);
    if (i > 0) {
      els.push(<span key={'l' + i} style={{ position: 'absolute', left: (i - 1) * PITCH, top: midY - 0.5,
        width: PITCH, height: 1, background: i <= spineCurrent ? INK_1 : LINE_ROW }} />);
    }
    els.push(<Marker key={'s' + i} x={i * PITCH} y={midY} w={c.w} bg={c.bg} bd={c.bd}
      title={THREAD_SPINE_NODES[i]} />);
    els.push(
      <span key={'t' + i} style={{ position: 'absolute', left: i * PITCH - 26, top: i % 2 ? 72 : 58,
        width: 52, textAlign: 'center', fontSize: 8, textTransform: 'uppercase',
        letterSpacing: '0.06em', whiteSpace: 'nowrap',
        fontWeight: kind === 'current' ? 500 : 400,
        color: kind === 'current' ? INK_1 : kind === 'done' ? INK_3 : INK_4 }}>
        {THREAD_SPINE_NODES[i]}
      </span>
    );
  }

  var splitX = spineW;
  var forkX = splitX + SPLIT;

  return (
    // paddingLeft 28: the first spine label ("SPECIFYING") is centered on
    // node x=0 (left: −26 inside the relative box). ItemForm mounts this
    // in an overflow-x:auto container, and scroll containers clip
    // negative-offset content — you can never scroll left of zero — so
    // without the pad the label rendered as "ECIFYING" on every item page.
    <div style={{ width: spineW + SPLIT + FORK + JOIN + 60, paddingLeft: 28 }}>
      <div style={{ position: 'relative', height: 96 }}>
        {els}
        <svg width={SPLIT} height="96" style={{ position: 'absolute', left: splitX, top: 0 }}>
          <path d={'M0 ' + midY + ' C12 ' + midY + ' 12 ' + topY + ' ' + SPLIT + ' ' + topY}
            stroke={forked ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
          <path d={'M0 ' + midY + ' C12 ' + midY + ' 12 ' + botY + ' ' + SPLIT + ' ' + botY}
            stroke={moneyStarted ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
        </svg>

        {/* inventory rail */}
        <span style={{ position: 'absolute', left: forkX, top: topY - 0.5, width: FORK, height: 1, background: LINE_ROW }} />
        {forked && st.inventoryIndex >= 0 && (
          <span style={{ position: 'absolute', left: forkX, top: topY - 0.5,
            width: st.inventoryIndex >= 4 ? FORK : invX[st.inventoryIndex], height: 1, background: INK_1 }} />
        )}
        {THREAD_INVENTORY_NODES.map(function(t, i) {
          var kind = st.inventoryIndex >= 0 && i < st.inventoryIndex ? 'done'
            : i === st.inventoryIndex ? 'current' : 'future';
          var c = circleStyle(kind, ring, sizes);
          return [
            <Marker key={'i' + i} x={forkX + invX[i]} y={topY} w={c.w} bg={c.bg} bd={c.bd} title={t} />,
            <span key={'it' + i} style={{ position: 'absolute', left: forkX + invX[i] - 33, top: 2,
              width: 66, textAlign: 'center', fontSize: 8, textTransform: 'uppercase',
              letterSpacing: '0.06em', whiteSpace: 'nowrap',
              fontWeight: i === st.inventoryIndex ? 500 : 400,
              color: i === st.inventoryIndex ? INK_1 : kind === 'done' ? INK_3 : INK_4 }}>{t}</span>,
          ];
        })}

        {/* money rail */}
        <span style={{ position: 'absolute', left: forkX, top: botY - 0.5, width: FORK, height: 1, background: LINE_ROW }} />
        {moneyStarted && (
          <span style={{ position: 'absolute', left: forkX, top: botY - 0.5,
            width: mStates[1] === 'done' ? (mStates[2] === 'done' ? FORK : monX[1]) : monX[0],
            height: 1, background: INK_1 }} />
        )}
        {THREAD_MONEY_NODES.map(function(t, i) {
          var sq = squareStyle(mStates[i], { done: 6, due: 7, future: 5 });
          var label = t + (mStates[i] === 'done' ? ' · paid' : mStates[i] === 'due' || mStates[i] === 'alert' ? ' · due' : '');
          if (i === 1 && mStates[i] === 'done') label = t.replace(' sent', '') + ' · sent';
          var lc = mStates[i] === 'alert' ? ALERT_RED_TEXT
            : mStates[i] === 'due' ? ALERT_AMBER_TEXT
            : mStates[i] === 'done' ? INK_3 : INK_4;
          return [
            <Marker key={'m' + i} square x={forkX + monX[i]} y={botY} w={sq.w} bg={sq.bg} bd={sq.bd} title={t} />,
            <span key={'mt' + i} style={{ position: 'absolute', left: forkX + monX[i] - 48, top: 86,
              width: 96, textAlign: 'center', fontSize: 8, textTransform: 'uppercase',
              letterSpacing: '0.06em', whiteSpace: 'nowrap',
              fontWeight: mStates[i] === 'due' || mStates[i] === 'alert' ? 500 : 400, color: lc }}>{label}</span>,
          ];
        })}

        {/* rail captions */}
        {props.railCaptions !== false && [
          <span key="c1" style={{ position: 'absolute', left: forkX, top: 24, fontSize: 7.5,
            textTransform: 'uppercase', letterSpacing: '0.06em', color: INK_4 }}>Inventory · marked by humans</span>,
          <span key="c2" style={{ position: 'absolute', left: forkX, top: 62, fontSize: 7.5,
            textTransform: 'uppercase', letterSpacing: '0.06em', color: INK_4 }}>Money · derived from records</span>,
        ]}

        <svg width={JOIN} height="96" style={{ position: 'absolute', left: forkX + FORK, top: 0 }}>
          <path d={'M0 ' + topY + ' C12 ' + topY + ' 12 ' + midY + ' ' + JOIN + ' ' + midY}
            stroke={st.complete ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
          <path d={'M0 ' + botY + ' C12 ' + botY + ' 12 ' + midY + ' ' + JOIN + ' ' + midY}
            stroke={st.complete ? INK_1 : LINE_ROW} strokeWidth="1" fill="none" />
        </svg>
        <span title="Complete" style={{ position: 'absolute', left: forkX + FORK + JOIN + 2,
          top: midY - (st.complete ? 6 : 3),
          width: st.complete ? 12 : 6, height: st.complete ? 12 : 6, borderRadius: '50%',
          boxSizing: 'border-box', background: SURFACE,
          border: st.complete ? '1.5px solid ' + ZONE_GREEN : '1px solid ' + LINE_SHELL,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 7, color: ZONE_GREEN, lineHeight: 1 }}>{st.complete ? '✓' : ''}</span>
        <span style={{ position: 'absolute', left: forkX + FORK + JOIN - 16, top: 58, width: 48,
          textAlign: 'center', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: st.complete ? ZONE_GREEN : INK_4, fontWeight: st.complete ? 500 : 400 }}>Complete</span>
      </div>

      {/* money summary line — silent green shows the ring alone (color
          instead of words, Cimon 2026-07-14); text is for exceptions. */}
      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span title={st.health.summary} style={{ width: 9, height: 9, borderRadius: '50%', boxSizing: 'border-box',
          background: SURFACE, border: '1.5px solid ' + ring, flexShrink: 0 }} />
        {!(st.health.silent && !props.summary) && (
          <span style={{ fontSize: 11, color: st.health.ring === 'red' ? ALERT_RED_TEXT
            : st.health.ring === 'amber' ? ALERT_AMBER_TEXT
            : st.health.ring === 'yellow' ? ALERT_AMBER_TEXT
            : st.health.complete ? ZONE_GREEN : '#555552' }}>
            {props.summary || st.health.summary}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Thread(props) {
  if (!props.state) return null;
  if (props.size === 'micro') return <MicroThread state={props.state} />;
  if (props.size === 'full') return <FullThread state={props.state} summary={props.summary} railCaptions={props.railCaptions} />;
  return <RowThread state={props.state} />;
}
